import React, { useState } from "react";
import { useLocation } from "react-router-dom";

const generateBoard = () => {
  const elements = [
    "H", "He", "Li", "Be", "B", "C", "N", "O", "F", "Ne", "Na", "Mg",
    "Al", "Si", "P", "S", "Cl", "Ar", "K", "Ca", "Sc", "Ti", "V", "Cr",
    "Mn", "Fe", "Co", "Ni", "Cu", "Zn", "Ga", "Ge", "As", "Se", "Br", "Kr",
    "Rb", "Sr", "Y", "Zr", "Nb", "Mo", "Tc", "Ru", "Rh", "Pd", "Ag", "Cd",
  ];
  return Array.from({ length: 48 }, (_, i) => ({
    name: elements[i % elements.length],
    type: i % 8 === 0 ? "quiz" : "collect",
  }));
};

const quizQuestions = [
  {
    question: "What is the atomic number of Hydrogen?",
    options: ["1", "2", "3", "4"],
    correctAnswer: "1",
  },
  {
    question: "Which element is known as the noble gas?",
    options: ["Oxygen", "Neon", "Nitrogen", "Helium"],
    correctAnswer: "Neon",
  },
  {
    question: "What is the chemical symbol for Gold?",
    options: ["Ag", "Au", "Pb", "Fe"],
    correctAnswer: "Au",
  },
  // Add more questions as needed
];

const PTBoardGame = () => {
  const location = useLocation();
  const players = location.state.players;
  const [board] = useState(generateBoard());
  const [positions, setPositions] = useState(Array(players.length).fill(0));
  const [scores, setScores] = useState(Array(players.length).fill(0));
  const [currentTurn, setCurrentTurn] = useState(0);
  const [diceRoll, setDiceRoll] = useState(null);
  const [message, setMessage] = useState("");
  const [quizActive, setQuizActive] = useState(false);
  const [quizQuestion, setQuizQuestion] = useState(null);
  const [quizAnswer, setQuizAnswer] = useState("");
  const [showLeaderboard, setShowLeaderboard] = useState(false);

  const rollDice = () => {
    const roll = Math.floor(Math.random() * 6) + 1;
    setDiceRoll(roll);

    const newPosition = (positions[currentTurn] + roll) % board.length;
    const tile = board[newPosition];

    setPositions((prev) =>
      prev.map((pos, index) => (index === currentTurn ? newPosition : pos))
    );

    if (tile.type === "quiz") {
      startQuiz(tile.name);
    } else if (tile.type === "collect") {
      setMessage(`${players[currentTurn]} collected ${tile.name}!`);
      setScores((prevScores) =>
        prevScores.map((score, index) =>
          index === currentTurn ? score + 10 : score
        )
      );
      nextTurn();
    }
  };

  const startQuiz = (element) => {
    setQuizActive(true);
    const randomQuestion = quizQuestions[Math.floor(Math.random() * quizQuestions.length)];
    setQuizQuestion(randomQuestion);
    setMessage(`${players[currentTurn]} landed on a quiz tile: Answer the question!`);
  };

  const submitQuizAnswer = () => {
    if (quizAnswer === quizQuestion.correctAnswer) {
      setMessage("Correct Answer!");
      setScores((prevScores) =>
        prevScores.map((score, index) =>
          index === currentTurn ? score + 20 : score
        )
      );
    } else {
      setMessage("Incorrect Answer!");
    }
    setQuizActive(false);
    nextTurn();
  };

  const nextTurn = () => {
    setTimeout(() => {
      setCurrentTurn((currentTurn + 1) % players.length);
      setMessage("");
      setDiceRoll(null);
    }, 3000);
  };

  const toggleLeaderboard = () => {
    setShowLeaderboard(!showLeaderboard);
  };

  return (  
    <div className="flex flex-col items-center justify-center h-screen-full bg-gray-50 [&_p]:text-white">
      <h1 className="text-4xl font-bold mt-4 mb-4 text-blue-800">Periodic Pursuit</h1>
      {showLeaderboard ? (
        <div className="bg-white p-6 rounded shadow mb-6 w-full max-w-md">
          <h2 className="text-xl font-bold text-center mb-4">Leaderboard</h2>
          <ul className="space-y-2 [&_li]:text-white">
            {players.map((player, index) => (
              <li key={index} className="flex justify-between">
                {player}: {scores[index]} points
              </li>
            ))}
          </ul>
          <button
            onClick={toggleLeaderboard}
            className="mt-4 bg-blue-500 text-white px-4 py-2 rounded w-full"
          >
            Close Leaderboard
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-12 gap-1 w-full max-w-4xl mb-6">
            {board.map((tile, index) => (
              <div
                key={index}
                className={`border rounded p-2 flex items-center justify-center h-16 ${
                  positions.includes(index) ? "bg-yellow-300" : "bg-blue-100"
                }`}
              >
                {tile.name}
              </div>
            ))}
          </div>
          <div className="w-full max-w-xl bg-white p-6 rounded shadow mb-6">
            <h2 className="text-xl font-bold text-center mb-4 text-blue-800">
              Current Turn: {players[currentTurn]}
            </h2>
            <div className="flex justify-between items-center mb-4">
              <p className="text-gray-700">Dice Roll: {diceRoll || "-"}</p>
              <p className="text-gray-700">Message: {message || "Roll the dice!"}</p>
            </div>
            <button
              onClick={rollDice}
              className="bg-green-500 text-white px-4 py-2 rounded w-full"
              disabled={diceRoll !== null}
            >
              Roll Dice
            </button>
          </div>
        </>
      )}
      {quizActive && (
        <div className="bg-white p-6 rounded shadow w-full max-w-md mt-6">
          <h3 className="text-xl font-bold text-center mb-4">Quiz Time!</h3>
          <p className="text-lg mb-4">{quizQuestion.question}</p>
          <div className="space-y-2">
            {quizQuestion.options.map((option, index) => (
              <button
                key={index}
                onClick={() => setQuizAnswer(option)}
                className={`block w-full px-4 py-2 rounded ${
                  quizAnswer === option ? "bg-blue-500 text-white" : "bg-gray-200"
                }`}
              >
                {option}
              </button>
            ))}
          </div>
          <button
            onClick={submitQuizAnswer}
            className="mt-4 bg-blue-500 text-white px-4 py-2 rounded w-full"
          >
            Submit Answer
          </button>
        </div>
      )}
      <button
        onClick={toggleLeaderboard}
        className="mt-6 bg-gray-500 text-white px-4 py-2 rounded w-full"
      >
        {showLeaderboard ? "Hide Leaderboard" : "Show Leaderboard"}
      </button>
    </div>
  );
};

export default PTBoardGame;
